import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { format } from 'date-fns';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  Users,
  Download,
  FileText,
  Loader2,
  Calendar as CalendarIcon,
  X,
  ChevronDown,
  ChevronRight,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Upload,
  Share2,
  Link,
  Copy,
  Clock,
  Eye,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1E40AF', '#D97757', '#2563EB', '#6366F1', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6'];

const formatCurrency = (value) => {
  if (value == null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyFull = (value) => {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

function StatCard({ label, value, icon: Icon, iconBg, iconColor, change, testId }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500 truncate">{label}</p>
          <p className="text-2xl font-semibold mt-1 truncate">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${change >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
              {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{Math.abs(change).toFixed(1)}% vs prior</span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0 ml-3`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-900 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Reusable Export/Import Bar ──
function ExportImportBar({ onExportCSV, onExportPDF, onImport, exporting, importing, testIdPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 px-5 py-3 shadow-sm" data-testid={`${testIdPrefix}-actions`}>
      <Button
        onClick={onExportCSV}
        disabled={exporting}
        data-testid={`${testIdPrefix}-export-csv`}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        size="sm"
      >
        {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
        Export Excl
      </Button>
      <Button
        onClick={onExportPDF}
        disabled={exporting}
        data-testid={`${testIdPrefix}-export-pdf`}
        className="bg-gray-700 hover:bg-gray-800 text-white"
        size="sm"
      >
        {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
        Export Pdf
      </Button>
      <label className="cursor-pointer">
        <Button
          asChild
          disabled={importing}
          data-testid={`${testIdPrefix}-import-csv`}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          size="sm"
        >
          <span>
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Import CSV
          </span>
        </Button>
        <input
          type="file"
          accept=".csv"
          className="hidden"
          onChange={onImport}
          disabled={importing}
        />
      </label>
    </div>
  );
}

// ── Overview Tab ──
function OverviewSection({ overview, loading, onExportCSV, onExportPDF, onImport, exporting, importing }) {
  if (loading) return <LoadingSkeleton />;
  if (!overview) return <EmptyState message="No data available" />;

  const trendData = (overview.trends || []).map(t => ({
    ...t,
    month: t.month?.substring(5) || t.month,
  }));

  return (
    <div className="space-y-6" data-testid="reports-overview-section">
      <ExportImportBar
        onExportCSV={onExportCSV}
        onExportPDF={onExportPDF}
        onImport={onImport}
        exporting={exporting}
        importing={importing}
        testIdPrefix="overview"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Revenue"
          value={formatCurrency(overview.total_revenue)}
          icon={DollarSign}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          testId="stat-total-revenue"
        />
        <StatCard
          label="Total Expenses"
          value={formatCurrency(overview.total_expenses)}
          icon={TrendingDown}
          iconBg="bg-red-50"
          iconColor="text-red-500"
          testId="stat-total-expenses"
        />
        <StatCard
          label="Net Profit"
          value={formatCurrency(overview.net_profit)}
          icon={TrendingUp}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          testId="stat-net-profit"
        />
        <StatCard
          label="Cash on Hand"
          value={formatCurrency(overview.cash_on_hand)}
          icon={Wallet}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          testId="stat-cash-on-hand"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-receivable">
          <p className="text-sm text-gray-500">Accounts Receivable</p>
          <p className="text-xl font-semibold mt-1">{formatCurrency(overview.total_receivable)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-payments">
          <p className="text-sm text-gray-500">Total Payments</p>
          <p className="text-xl font-semibold mt-1">{formatCurrency(overview.total_payments)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-total-clients">
          <p className="text-sm text-gray-500">Total Clients</p>
          <p className="text-xl font-semibold mt-1">{overview.total_clients}</p>
        </div>
      </div>

      {trendData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-base font-medium text-gray-900 mb-4">Revenue vs Expenses (12 Months)</h3>
          <div className="h-72" data-testid="revenue-expense-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1E40AF" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#1E40AF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D97757" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#D97757" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#1E40AF" fill="url(#gradRevenue)" strokeWidth={2} />
                <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#D97757" fill="url(#gradExpenses)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── P&L Tab ──
function PnlSection({ pnl, loading, onExportCSV, onExportPDF, onImport, exporting, importing }) {
  if (loading) return <LoadingSkeleton />;
  if (!pnl?.current) return <EmptyState message="No P&L data available" />;

  const { current, comparison } = pnl;
  const revenueChange = comparison ? ((current.revenue - comparison.revenue) / (comparison.revenue || 1)) * 100 : null;
  const expenseChange = comparison ? ((current.expenses - comparison.expenses) / (comparison.expenses || 1)) * 100 : null;

  return (
    <div className="space-y-6" data-testid="reports-pnl-section">
      <ExportImportBar
        onExportCSV={onExportCSV}
        onExportPDF={onExportPDF}
        onImport={onImport}
        exporting={exporting}
        importing={importing}
        testIdPrefix="pnl"
      />
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-medium text-gray-900">Profit & Loss Statement</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {current.period.start} to {current.period.end}
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {/* Revenue */}
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Revenue</h4>
              {revenueChange !== null && (
                <span className={`text-xs flex items-center gap-1 ${revenueChange >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                  {revenueChange >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(revenueChange).toFixed(1)}%
                </span>
              )}
            </div>
            {current.revenue_items?.map((item, i) => (
              <div key={i} className="flex justify-between py-1.5 text-sm">
                <span className="text-gray-600">{item.category}</span>
                <span className="font-medium text-gray-900">{formatCurrencyFull(item.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 mt-2 border-t border-gray-100 text-sm font-semibold">
              <span className="text-gray-900">Total Revenue</span>
              <span className="text-blue-700">{formatCurrencyFull(current.revenue)}</span>
            </div>
          </div>

          {/* Expenses */}
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Expenses</h4>
              {expenseChange !== null && (
                <span className={`text-xs flex items-center gap-1 ${expenseChange <= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                  {expenseChange <= 0 ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                  {Math.abs(expenseChange).toFixed(1)}%
                </span>
              )}
            </div>
            {current.expense_items?.length > 0 ? (
              current.expense_items.map((item, i) => (
                <div key={i} className="flex justify-between py-1.5 text-sm">
                  <span className="text-gray-600">{item.category}</span>
                  <span className="font-medium text-gray-900">{formatCurrencyFull(item.amount)}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 py-1">No expenses recorded</p>
            )}
            <div className="flex justify-between pt-3 mt-2 border-t border-gray-100 text-sm font-semibold">
              <span className="text-gray-900">Total Expenses</span>
              <span className="text-red-600">{formatCurrencyFull(current.expenses)}</span>
            </div>
          </div>

          {/* Net Profit */}
          <div className="p-5 bg-gray-50/50">
            <div className="flex justify-between text-base font-bold">
              <span className="text-gray-900">Net Profit</span>
              <span className={current.net_profit >= 0 ? 'text-blue-700' : 'text-red-600'}>
                {formatCurrencyFull(current.net_profit)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison */}
      {comparison && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Prior Period ({comparison.period.start} to {comparison.period.end})
          </h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500">Revenue</p>
              <p className="text-sm font-semibold mt-0.5">{formatCurrency(comparison.revenue)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Expenses</p>
              <p className="text-sm font-semibold mt-0.5">{formatCurrency(comparison.expenses)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Net Profit</p>
              <p className={`text-sm font-semibold mt-0.5 ${comparison.net_profit >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                {formatCurrency(comparison.net_profit)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Balance Sheet Tab ──
function BalanceSheetSection({ balanceSheet, loading, onExportCSV, onExportPDF, onImport, exporting, importing }) {
  if (loading) return <LoadingSkeleton />;
  if (!balanceSheet) return <EmptyState message="No balance sheet data" />;

  const bs = balanceSheet;

  const LineItem = ({ label, amount, bold, color }) => (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? 'font-semibold border-t border-gray-200 pt-3 mt-2' : ''}`}>
      <span className={bold ? 'text-gray-900' : 'text-gray-600 pl-3'}>{label}</span>
      <span className={`font-medium ${color || 'text-gray-900'}`}>{formatCurrencyFull(amount)}</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-balance-sheet-section">
      <ExportImportBar
        onExportCSV={onExportCSV}
        onExportPDF={onExportPDF}
        onImport={onImport}
        exporting={exporting}
        importing={importing}
        testIdPrefix="balance"
      />
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <div>
            <h3 className="text-base font-medium text-gray-900">Balance Sheet</h3>
            <p className="text-sm text-gray-500 mt-0.5">As of {bs.as_of_date}</p>
          </div>
          {bs.balanced && (
            <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full font-medium" data-testid="balance-sheet-balanced">
              Balanced
            </span>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          <div className="p-5">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Assets</h4>
            <LineItem label="Cash" amount={bs.assets.cash} />
            <LineItem label="Accounts Receivable" amount={bs.assets.accounts_receivable} />
            <LineItem label="Total Assets" amount={bs.assets.total} bold color="text-blue-700" />
          </div>

          <div className="p-5">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Liabilities</h4>
            <LineItem label="Accounts Payable" amount={bs.liabilities.accounts_payable} />
            <LineItem label="Total Liabilities" amount={bs.liabilities.total} bold color="text-red-600" />
          </div>

          <div className="p-5">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Equity</h4>
            <LineItem label="Retained Earnings" amount={bs.equity.retained_earnings} />
            <LineItem label="Total Equity" amount={bs.equity.total} bold color="text-blue-700" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Expenses Tab (Expense Report) ──
function ExpenseReportSection({ categories, loading, companyName, onExportCSV, onExportPDF, onImport, exporting, importing, companyId }) {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [localFromDate, setLocalFromDate] = useState(null);
  const [localToDate, setLocalToDate] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState({ category: 'all', from: null, to: null });
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const handleDownloadFilteredCsv = async () => {
    setDownloadingCsv(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (appliedFilter.from) params.start_date = format(appliedFilter.from, 'yyyy-MM-dd');
      if (appliedFilter.to) params.end_date = format(appliedFilter.to, 'yyyy-MM-dd');
      if (appliedFilter.category && appliedFilter.category !== 'all') params.category = appliedFilter.category;
      const res = await axios.get(`${API_URL}/api/reports/export/expenses`, {
        params, withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const parts = ['expenses'];
      if (appliedFilter.category && appliedFilter.category !== 'all') parts.push(appliedFilter.category);
      if (appliedFilter.from) parts.push(format(appliedFilter.from, 'yyyy-MM-dd'));
      if (appliedFilter.to) parts.push(format(appliedFilter.to, 'yyyy-MM-dd'));
      a.download = parts.join('_') + '.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Expense report exported');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    } finally {
      setDownloadingCsv(false);
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (!categories) return <EmptyState message="No expense data available" />;

  const expCats = categories.expense_categories || [];

  // Flatten all transactions from categories
  const allTransactions = expCats.flatMap(cat =>
    (cat.transactions || []).map(t => ({
      ...t,
      category: cat.category,
    }))
  );

  // Apply filters
  const filteredTransactions = allTransactions.filter(t => {
    if (appliedFilter.category !== 'all' && t.category !== appliedFilter.category) return false;
    if (appliedFilter.from && t.date < format(appliedFilter.from, 'yyyy-MM-dd')) return false;
    if (appliedFilter.to && t.date > format(appliedFilter.to, 'yyyy-MM-dd')) return false;
    return true;
  }).sort((a, b) => b.date.localeCompare(a.date));

  const filteredTotal = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);

  const handleApplyFilters = () => {
    setAppliedFilter({ category: categoryFilter, from: localFromDate, to: localToDate });
  };

  const handleYTD = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    setLocalFromDate(startOfYear);
    setLocalToDate(now);
    setAppliedFilter({ category: categoryFilter, from: startOfYear, to: now });
  };

  return (
    <div className="space-y-5" data-testid="reports-expenses-section">
      {/* Title */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Expense Report</h3>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
        {/* Row 1: Category + Company */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-blue-600 mb-1.5 block" data-testid="choose-category-label">Choose Category</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="expense-category-filter" className="border-gray-300">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({expCats.length})</SelectItem>
                {expCats.map((cat, i) => (
                  <SelectItem key={i} value={cat.category}>{cat.category} ({cat.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-blue-600 mb-1.5 block">Company</label>
            <div className="h-10 border border-gray-300 rounded-md flex items-center px-3 text-sm text-gray-700 bg-white">
              {companyName || 'All'}
            </div>
          </div>
        </div>

        {/* Row 2: Date Range */}
        <div className="flex flex-col sm:flex-row items-end gap-3">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">From Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="expense-from-date"
                  className={`w-full justify-start border-gray-300 font-normal ${!localFromDate && 'text-gray-400'}`}
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {localFromDate ? format(localFromDate, 'MM/dd/yyyy') : 'mm/dd/yyyy'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={localFromDate} onSelect={setLocalFromDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">To Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="expense-to-date"
                  className={`w-full justify-start border-gray-300 font-normal ${!localToDate && 'text-gray-400'}`}
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {localToDate ? format(localToDate, 'MM/dd/yyyy') : 'mm/dd/yyyy'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={localToDate} onSelect={setLocalToDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <Button
            onClick={handleYTD}
            data-testid="ytd-button"
            className="bg-blue-500 hover:bg-blue-600 text-white px-5 h-10 shrink-0"
          >
            YTD
          </Button>
        </div>

        {/* Row 3: Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-1">
          <Button
            onClick={handleDownloadFilteredCsv}
            disabled={downloadingCsv}
            data-testid="expense-export-excel-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6"
          >
            {downloadingCsv ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button
            onClick={onExportPDF}
            disabled={exporting}
            data-testid="expense-export-pdf-btn"
            className="bg-gray-700 hover:bg-gray-800 text-white px-6"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
            Export Pdf
          </Button>
          <label className="cursor-pointer">
            <Button
              asChild
              disabled={importing}
              data-testid="expense-import-csv-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white px-6"
            >
              <span>
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Import CSV
              </span>
            </Button>
            <input type="file" accept=".csv" className="hidden" onChange={onImport} disabled={importing} />
          </label>
          <Button
            onClick={handleApplyFilters}
            data-testid="apply-filters-btn"
            className="bg-blue-500 hover:bg-blue-600 text-white px-10 flex-1 sm:flex-none"
          >
            Apply Filters
          </Button>
        </div>
      </div>

      {/* Expense Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-600 hover:bg-blue-600">
              <TableHead className="text-white font-semibold text-xs uppercase tracking-wider">Category</TableHead>
              <TableHead className="text-white font-semibold text-xs uppercase tracking-wider">Payee</TableHead>
              <TableHead className="text-white font-semibold text-xs uppercase tracking-wider">Date</TableHead>
              <TableHead className="text-white font-semibold text-xs uppercase tracking-wider text-right">Amount</TableHead>
              <TableHead className="text-white font-semibold text-xs uppercase tracking-wider">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTransactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-16 text-gray-400">
                  No data available in table
                </TableCell>
              </TableRow>
            ) : (
              filteredTransactions.map((t, idx) => (
                <TableRow key={t.id || idx} className="hover:bg-gray-50" data-testid={`expense-row-${idx}`}>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      t.category === 'Fixed'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      {t.category}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-700">{t.description}</TableCell>
                  <TableCell className="text-sm text-gray-600">{t.date}</TableCell>
                  <TableCell className="text-sm text-gray-900 font-medium text-right">{formatCurrencyFull(t.amount)}</TableCell>
                  <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{t.notes || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Total Row */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-5 py-3 shadow-sm" data-testid="expense-report-total">
        <span className="text-base font-semibold text-blue-600">Total:</span>
        <div className="flex items-center gap-3">
          <div className="w-48 h-px bg-blue-300" />
          <span className="text-base font-bold text-gray-900">{formatCurrencyFull(filteredTotal)}</span>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
            <div className="h-7 bg-gray-200 rounded w-32" />
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-48 mb-4" />
        <div className="h-64 bg-gray-100 rounded" />
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
      <p className="text-gray-500">{message}</p>
    </div>
  );
}

export default function ReportsPage() {
  const { currentCompany } = useCompany();
  const [activeTab, setActiveTab] = useState('overview');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [categories, setCategories] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [deleteShareTarget, setDeleteShareTarget] = useState(null);

  const buildParams = useCallback(() => {
    const params = {};
    if (currentCompany?.id) params.company_id = currentCompany.id;
    if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
    if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');
    return params;
  }, [currentCompany, startDate, endDate]);

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const params = buildParams();

      if (activeTab === 'overview') {
        const res = await axios.get(`${API_URL}/api/reports/overview`, { params, withCredentials: true });
        setOverview(res.data);
      } else if (activeTab === 'pnl') {
        const res = await axios.get(`${API_URL}/api/reports/pnl`, { params, withCredentials: true });
        setPnl(res.data);
      } else if (activeTab === 'balance') {
        const bsParams = { ...params };
        if (params.end_date) bsParams.as_of_date = params.end_date;
        delete bsParams.start_date;
        delete bsParams.end_date;
        const res = await axios.get(`${API_URL}/api/reports/balance-sheet`, { params: bsParams, withCredentials: true });
        setBalanceSheet(res.data);
      } else if (activeTab === 'expenses') {
        const res = await axios.get(`${API_URL}/api/reports/categories`, { params, withCredentials: true });
        setCategories(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentCompany, buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = async (reportType) => {
    setExporting(true);
    try {
      const params = buildParams();
      let endpoint = `${API_URL}/api/reports/export/csv`;
      if (reportType === 'overview') {
        endpoint = `${API_URL}/api/reports/export/overview-csv`;
      } else {
        params.report_type = reportType;
      }
      const response = await axios.get(endpoint, {
        params,
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('CSV export failed:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = {};
      if (currentCompany?.id) params.company_id = currentCompany.id;
      const res = await axios.post(`${API_URL}/api/reports/import/csv`, formData, {
        params,
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { imported, errors } = res.data;
      if (imported > 0) {
        toast.success(`Imported ${imported} expense${imported > 1 ? 's' : ''} successfully`);
        fetchData();
      }
      if (errors?.length > 0) {
        toast.error(`${errors.length} row(s) had errors`);
      }
      if (imported === 0 && (!errors || errors.length === 0)) {
        toast.info('No records found in CSV');
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      await import('jspdf-autotable');
      const doc = new jsPDF();
      const companyName = currentCompany?.name || 'All Companies';
      const dateStr = new Date().toLocaleDateString();

      doc.setFontSize(18);
      doc.text(`${companyName} - Financial Report`, 14, 22);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated on ${dateStr}`, 14, 30);

      let y = 40;

      if (activeTab === 'overview' && overview) {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Overview', 14, y);
        y += 8;
        doc.autoTable({
          startY: y,
          head: [['Metric', 'Value']],
          body: [
            ['Total Revenue', formatCurrencyFull(overview.total_revenue)],
            ['Total Expenses', formatCurrencyFull(overview.total_expenses)],
            ['Net Profit', formatCurrencyFull(overview.net_profit)],
            ['Cash on Hand', formatCurrencyFull(overview.cash_on_hand)],
            ['Accounts Receivable', formatCurrencyFull(overview.total_receivable)],
            ['Total Payments', formatCurrencyFull(overview.total_payments)],
            ['Total Clients', String(overview.total_clients)],
          ],
          theme: 'striped',
          headStyles: { fillColor: [44, 85, 69] },
        });
      } else if (activeTab === 'pnl' && pnl?.current) {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Profit & Loss Statement', 14, y);
        y += 8;
        const rows = [];
        rows.push(['', '']);
        rows.push(['REVENUE', '']);
        (pnl.current.revenue_items || []).forEach(item => {
          rows.push([`  ${item.category}`, formatCurrencyFull(item.amount)]);
        });
        rows.push(['Total Revenue', formatCurrencyFull(pnl.current.revenue)]);
        rows.push(['', '']);
        rows.push(['EXPENSES', '']);
        (pnl.current.expense_items || []).forEach(item => {
          rows.push([`  ${item.category}`, formatCurrencyFull(item.amount)]);
        });
        rows.push(['Total Expenses', formatCurrencyFull(pnl.current.expenses)]);
        rows.push(['', '']);
        rows.push(['NET PROFIT', formatCurrencyFull(pnl.current.net_profit)]);
        doc.autoTable({
          startY: y,
          head: [['Item', 'Amount']],
          body: rows,
          theme: 'plain',
          headStyles: { fillColor: [44, 85, 69], textColor: 255 },
          styles: { fontSize: 10 },
        });
      } else if (activeTab === 'balance' && balanceSheet) {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text(`Balance Sheet (as of ${balanceSheet.as_of_date})`, 14, y);
        y += 8;
        doc.autoTable({
          startY: y,
          head: [['Item', 'Amount']],
          body: [
            ['ASSETS', ''],
            ['  Cash', formatCurrencyFull(balanceSheet.assets.cash)],
            ['  Accounts Receivable', formatCurrencyFull(balanceSheet.assets.accounts_receivable)],
            ['Total Assets', formatCurrencyFull(balanceSheet.assets.total)],
            ['', ''],
            ['LIABILITIES', ''],
            ['  Accounts Payable', formatCurrencyFull(balanceSheet.liabilities.accounts_payable)],
            ['Total Liabilities', formatCurrencyFull(balanceSheet.liabilities.total)],
            ['', ''],
            ['EQUITY', ''],
            ['  Retained Earnings', formatCurrencyFull(balanceSheet.equity.retained_earnings)],
            ['Total Equity', formatCurrencyFull(balanceSheet.equity.total)],
          ],
          theme: 'plain',
          headStyles: { fillColor: [44, 85, 69], textColor: 255 },
        });
      } else if (activeTab === 'expenses' && categories) {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Expense Report', 14, y);
        y += 8;
        const expRows = [];
        (categories.expense_categories || []).forEach(cat => {
          (cat.transactions || []).forEach(t => {
            expRows.push([cat.category, t.description, t.date, formatCurrencyFull(t.amount), t.notes || '-']);
          });
        });
        doc.autoTable({
          startY: y,
          head: [['Category', 'Payee', 'Date', 'Amount', 'Notes']],
          body: expRows.length > 0 ? expRows : [['No data available', '', '', '', '']],
          theme: 'striped',
          headStyles: { fillColor: [5, 150, 105] },
        });
        const total = (categories.expense_categories || []).reduce((s, c) => s + c.total, 0);
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(`Total: ${formatCurrencyFull(total)}`, 14, finalY);
      }

      doc.save(`${companyName.toLowerCase().replace(/\s+/g, '_')}_${activeTab}_report.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // --- Share Report ---
  const fetchSharedLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/shares`, { withCredentials: true });
      setSharedLinks(res.data);
    } catch { /* ignore */ }
  }, []);

  const handleShareReport = async () => {
    setSharing(true);
    try {
      const params = buildParams();
      const reportTypeMap = { overview: 'overview', pnl: 'pnl', balance: 'balance_sheet', expenses: 'overview' };
      const res = await axios.post(`${API_URL}/api/reports/share`, {
        report_type: reportTypeMap[activeTab] || 'overview',
        company_id: currentCompany?.id,
        start_date: params.start_date || null,
        end_date: params.end_date || null,
        expires_in_days: 7
      }, { withCredentials: true });
      const shareUrl = `${window.location.origin}/shared-report/${res.data.token}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied to clipboard!');
      } catch {
        toast.success('Share link created! Open Shared Links to copy it.');
      }
      fetchSharedLinks();
    } catch { toast.error('Failed to generate share link'); }
    finally { setSharing(false); }
  };

  const handleDeleteShare = async () => {
    if (!deleteShareTarget) return;
    try {
      await axios.delete(`${API_URL}/api/reports/shares/${deleteShareTarget.id}`, { withCredentials: true });
      toast.success('Share link revoked');
      setDeleteShareTarget(null);
      fetchSharedLinks();
    } catch { toast.error('Failed to delete share link'); }
  };

  useEffect(() => { if (shareDialogOpen) fetchSharedLinks(); }, [shareDialogOpen, fetchSharedLinks]);

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a company</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="text-gray-500 mt-1">Financial insights for {currentCompany.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)} data-testid="manage-shares-btn">
            <Link className="w-4 h-4 mr-2" />Shared Links
          </Button>
          <Button onClick={handleShareReport} disabled={sharing} data-testid="share-report-btn">
            {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
            Share Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="pnl" data-testid="tab-pnl">P&L</TabsTrigger>
            <TabsTrigger value="balance" data-testid="tab-balance">Balance Sheet</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2 ml-auto">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="report-start-date"
                className={`border-gray-200 text-sm ${!startDate && 'text-gray-400'}`}
              >
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                {startDate ? format(startDate, 'MMM d, yyyy') : 'Start'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
            </PopoverContent>
          </Popover>
          <Minus className="w-3 h-3 text-gray-400" />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="report-end-date"
                className={`border-gray-200 text-sm ${!endDate && 'text-gray-400'}`}
              >
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                {endDate ? format(endDate, 'MMM d, yyyy') : 'End'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
            </PopoverContent>
          </Popover>
          {(startDate || endDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setStartDate(null); setEndDate(null); }}
              className="text-gray-400 hover:text-gray-600 px-2"
              data-testid="clear-dates-btn"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewSection overview={overview} loading={loading} onExportCSV={() => handleExportCSV('overview')} onExportPDF={handleExportPDF} onImport={handleImportCSV} exporting={exporting} importing={importing} />}
      {activeTab === 'pnl' && <PnlSection pnl={pnl} loading={loading} onExportCSV={() => handleExportCSV('pnl')} onExportPDF={handleExportPDF} onImport={handleImportCSV} exporting={exporting} importing={importing} />}
      {activeTab === 'balance' && <BalanceSheetSection balanceSheet={balanceSheet} loading={loading} onExportCSV={() => handleExportCSV('balance_sheet')} onExportPDF={handleExportPDF} onImport={handleImportCSV} exporting={exporting} importing={importing} />}
      {activeTab === 'expenses' && <ExpenseReportSection categories={categories} loading={loading} companyName={currentCompany?.name} companyId={currentCompany?.id} onExportCSV={handleExportCSV} onExportPDF={handleExportPDF} onImport={handleImportCSV} exporting={exporting} importing={importing} />}

      {/* Shared Links Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Shared Report Links</DialogTitle>
          </DialogHeader>
          {sharedLinks.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Link className="w-8 h-8 mx-auto mb-2" />
              <p className="font-medium">No shared links yet</p>
              <p className="text-xs mt-1">Click "Share Report" to generate a link</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sharedLinks.map(s => {
                const isExpired = new Date(s.expires_at) < new Date();
                const shareUrl = `${window.location.origin}/shared-report/${s.token}`;
                return (
                  <div key={s.id} className={`p-3 rounded-lg border ${isExpired ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200'}`} data-testid={`shared-link-${s.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium capitalize">{s.report_type.replace('_', ' ')} — {s.company_name}</span>
                      <div className="flex items-center gap-1">
                        {!isExpired && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Copied!'); }} data-testid={`copy-share-${s.id}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteShareTarget(s)} data-testid={`delete-share-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{s.view_count} views</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{isExpired ? 'Expired' : `Expires ${new Date(s.expires_at).toLocaleDateString()}`}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Share Confirmation */}
      <AlertDialog open={!!deleteShareTarget} onOpenChange={() => setDeleteShareTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Shared Link</AlertDialogTitle>
            <AlertDialogDescription>This will permanently revoke access to this shared report. Anyone with the link will no longer be able to view it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteShare} className="bg-red-600 hover:bg-red-700">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
